import { useCallback, useEffect, useState } from 'react';
import {
  canShowAddToHomeScreen,
  clearDeferredInstallPrompt,
  dismissAddToHomeScreen,
  getAddToHomeScreenPlatform,
  getDeferredInstallPrompt,
  subscribeToInstallPrompt,
} from '../utils/addToHomeScreen';

const useAddToHomeScreen = ({ scopeId, requireStoreHost = true } = {}) => {
  const [visible, setVisible] = useState(() => canShowAddToHomeScreen(scopeId, { requireStoreHost }));
  const [canPrompt, setCanPrompt] = useState(() => Boolean(getDeferredInstallPrompt()));
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const platform = getAddToHomeScreenPlatform();

  const refreshVisibility = useCallback(() => {
    setVisible(canShowAddToHomeScreen(scopeId, { requireStoreHost }));
    setCanPrompt(Boolean(getDeferredInstallPrompt()));
  }, [requireStoreHost, scopeId]);

  useEffect(() => {
    refreshVisibility();
    return subscribeToInstallPrompt(refreshVisibility);
  }, [refreshVisibility]);

  const closeInstructions = useCallback(() => {
    setInstructionsOpen(false);
  }, []);

  const dismiss = useCallback(() => {
    dismissAddToHomeScreen(scopeId);
    setInstructionsOpen(false);
    setVisible(false);
  }, [scopeId]);

  const startInstall = useCallback(async () => {
    if (platform === 'ios') {
      setInstructionsOpen(true);
      return { outcome: 'ios-guide' };
    }

    const promptEvent = getDeferredInstallPrompt();
    if (promptEvent) {
      promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      clearDeferredInstallPrompt();
      setCanPrompt(false);
      if (choice?.outcome === 'accepted') {
        dismissAddToHomeScreen(scopeId);
        setVisible(false);
      }
      return { outcome: choice?.outcome || 'dismissed' };
    }

    return { outcome: 'unavailable' };
  }, [platform, scopeId]);

  return {
    visible,
    platform,
    canPrompt,
    instructionsOpen,
    closeInstructions,
    startInstall,
    dismiss,
  };
};

export default useAddToHomeScreen;
